import { HTTPError } from 'ky';
import { TriangleAlert, WifiOff } from 'lucide-react';
import { problemText, useApiProblem } from '@/shared/api';
import { Button } from '@/shared/components';

interface Props {
  error: unknown;
  /** Ohne Netz pausiert die Abfrage – dann sagt die Seite das, statt ewig zu laden. */
  paused: boolean;
  onRetry: () => void;
  retrying: boolean;
}

/** Laden der Buchung: Platzhalter; Fehler mit Grund und, wo es hilft, neuem Versuch. Nie endlos „Lade …“. */
export function BookingLoadState({ error, paused, onRetry, retrying }: Props) {
  const problem = useApiProblem(error);
  if (!error && paused) {
    return (
      <Notice
        icon="offline"
        text="Keine Verbindung – die Buchung lädt, sobald das Netz zurück ist."
      />
    );
  }
  if (!error) {
    return (
      <div
        role="status"
        aria-label="Buchung wird geladen"
        className="flex animate-pulse flex-col gap-4"
      >
        <div className="bg-forest-deep/20 rounded-hero h-48" />
        <div className="border-hairline rounded-tile h-40 border bg-white" />
      </div>
    );
  }
  // Der Status steht sofort fest; der Problem-Body kommt erst asynchron.
  const status = error instanceof HTTPError ? error.response.status : null;
  const text =
    status === 404
      ? 'Diese Buchung gibt es nicht oder nicht mehr in diesem Workspace.'
      : status === 403
        ? 'Diese Buchung liegt außerhalb Ihres Berechtigungsbereichs.'
        : problemText(problem, 'Die Buchung konnte nicht geladen werden.');
  return (
    <Notice
      icon="error"
      text={text}
      action={status === 403 || status === 404 ? undefined : { onRetry, retrying }}
    />
  );
}

/** Die Buchung steht da, aber das Nachladen schlug fehl: der Stand kann veraltet sein. */
export function BookingStaleNote({
  onRetry,
  retrying,
}: {
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div className="mb-5">
      <Notice
        icon="error"
        text="Die Buchung konnte nicht neu geladen werden – angezeigt wird der letzte bekannte Stand."
        action={{ onRetry, retrying }}
      />
    </div>
  );
}

function Notice({
  icon,
  text,
  action,
}: {
  icon: 'error' | 'offline';
  text: string;
  action?: { onRetry: () => void; retrying: boolean };
}) {
  const Icon = icon === 'offline' ? WifiOff : TriangleAlert;
  return (
    <div
      role="alert"
      className="border-hairline rounded-tile flex flex-wrap items-center gap-3 border bg-white p-4"
    >
      <Icon size={18} aria-hidden className="text-amber-ink flex-none" />
      <p className="min-w-0 flex-1 text-sm">{text}</p>
      {action && (
        <Button variant="secondary" onClick={action.onRetry} loading={action.retrying}>
          Erneut versuchen
        </Button>
      )}
    </div>
  );
}
